import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { useMediaQuery } from '@w/utils-fe'
import { hexToOklch, oklchToHex } from '../palette/utils'
import { DEFAULT_SOURCE_COLOR } from '../palette/types'
import { injectPaletteHues } from '../palette/palette-config'

type Theme = 'dark' | 'light' | 'system'

type ThemeProviderProps = {
  children: React.ReactNode
  defaultTheme?: Theme
  storageKey?: string
  reverse?: boolean // for storybook
  defaultMinimal?: boolean
  defaultSourceColor?: string
  minimal?: boolean
  sourceColor?: string
  onMinimalChange?: (minimal: boolean) => void
  onSourceColorChange?: (sourceColor: string) => void
}

type ThemeProviderState = {
  theme: Theme
  setTheme: (theme: Theme) => void
  minimal: boolean
  setMinimal: (minimal: boolean) => void
  sourceColor: string
  setSourceColor: (sourceHue: string) => void
}

const initialState: ThemeProviderState = {
  theme: 'system',
  // oxlint-disable-next-line typescript/strict-void-return -- stub no-op; returns null intentionally for ctx default
  setTheme: () => null,
  minimal: true,
  // oxlint-disable-next-line typescript/strict-void-return -- stub no-op; returns null intentionally for ctx default
  setMinimal: () => null,
  sourceColor: oklchToHex(DEFAULT_SOURCE_COLOR),
  // oxlint-disable-next-line typescript/strict-void-return -- stub no-op; returns null intentionally for ctx default
  setSourceColor: () => null,
}

const ThemeProviderContext = createContext(initialState)

// Blocked site data makes the localStorage getter, getItem and setItem throw a SecurityError.
function readStored(key: string): string | null {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

function writeStored(key: string, value: string) {
  try {
    localStorage.setItem(key, value)
  } catch {
    // Preference stays in memory only.
  }
}

export function ThemeProvider({
  children,
  defaultTheme = 'system',
  storageKey = 'vite-ui-theme',
  reverse = false,
  defaultMinimal = true,
  defaultSourceColor = oklchToHex(DEFAULT_SOURCE_COLOR),
  minimal: controlledMinimal,
  sourceColor: controlledSourceColor,
  onMinimalChange,
  onSourceColorChange,
  ...props
}: ThemeProviderProps) {
  const [theme, setTheme] = useState<Theme>(
    () => (readStored(storageKey) as Theme) || defaultTheme,
  )
  const [minimalState, setMinimalState] = useState<boolean>(() => {
    const stored = readStored(`${storageKey}-minimal`)
    return stored === null ? defaultMinimal : stored === 'true'
  })
  const [sourceColorState, setSourceColorState] = useState<string>(() => {
    const stored = readStored(`${storageKey}-sourceColor`)
    return stored ?? defaultSourceColor
  })
  const minimal = controlledMinimal ?? minimalState
  const sourceColor = controlledSourceColor ?? sourceColorState
  const systemPrefersDark = useMediaQuery('(prefers-color-scheme: dark)')

  const baseTheme =
    theme === 'system' ? (systemPrefersDark ? 'dark' : 'light') : theme
  const resolvedTheme = reverse
    ? baseTheme === 'dark'
      ? 'light'
      : 'dark'
    : baseTheme

  useEffect(() => {
    const root = document.documentElement
    root.classList.remove('dark', 'light')
    root.classList.add(resolvedTheme)
    root.classList.toggle('minimal', minimal)

    injectPaletteHues({
      sourceColor: hexToOklch(sourceColor),
      minimal,
      element: root,
      isDark: resolvedTheme === 'dark',
    })
  }, [resolvedTheme, sourceColor, minimal])

  const value = useMemo(
    () => ({
      theme: resolvedTheme,
      setTheme: (theme: Theme) => {
        writeStored(storageKey, theme)
        document.dispatchEvent(
          new CustomEvent('theme-change', { detail: { theme } }),
        )
        setTheme(theme)
      },
      minimal,
      setMinimal: (minimal: boolean) => {
        onMinimalChange?.(minimal)
        if (controlledMinimal !== undefined) return
        writeStored(`${storageKey}-minimal`, String(minimal))
        setMinimalState(minimal)
      },
      sourceColor,
      setSourceColor: (sourceColor: string) => {
        onSourceColorChange?.(sourceColor)
        if (controlledSourceColor !== undefined) return
        writeStored(`${storageKey}-sourceColor`, sourceColor)
        setSourceColorState(sourceColor)
      },
    }),
    [
      resolvedTheme,
      minimal,
      sourceColor,
      storageKey,
      controlledMinimal,
      controlledSourceColor,
      onMinimalChange,
      onSourceColorChange,
    ],
  )

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  )
}

export const useTheme = () => {
  const ctx = useContext(ThemeProviderContext)
  if (ctx === undefined)
    throw new Error('useTheme must be used within a ThemeProvider')
  return ctx
}
